import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Archive, Download, FileText, RefreshCw } from "lucide-react";

const CATEGORIES = ["SOP", "Arbeitsanweisung", "Rote-Hand-Brief", "Hygiene", "Prüfung"];

export default function QMPage() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", version: "1.0", category: "SOP", file_b64: null, filename: null, archived: false });
  const [syncing, setSyncing] = useState(false);

  const load = async () => setItems((await api.get("/qm")).data);
  useEffect(() => { load(); }, []);

  const syncAposoft = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/aposoft/sync-qm");
      toast.success(`${data.created} neue QM-Dokumente aus Aposoft importiert`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aposoft-Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm({ ...form, file_b64: r.result, filename: f.name });
    r.readAsDataURL(f);
  };

  const save = async () => {
    try { await api.post("/qm", form); toast.success("Gespeichert"); setDialog(false); setForm({ title: "", description: "", version: "1.0", category: "SOP", file_b64: null, filename: null, archived: false }); await load(); }
    catch (err) { toast.error("Fehler"); }
  };

  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/qm/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="qm-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Qualitätsmanagement</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">QM-Dokumente</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="qm-aposoft-sync" variant="outline" onClick={syncAposoft} disabled={syncing} className="rounded-full gap-2">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchronisiert…" : "Aposoft-Sync"}
          </Button>
          <Button data-testid="qm-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Dokument hochladen</Button>
        </div>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titel</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Erstellt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`qm-row-${d.id}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />{d.title}</div>
                    <div className="text-xs text-slate-500">{d.description}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{d.category}</Badge></TableCell>
                  <TableCell>{d.version}</TableCell>
                  <TableCell className="text-sm">{new Date(d.created_at).toLocaleDateString("de-DE")}</TableCell>
                  <TableCell>{d.archived ? <Badge variant="secondary">Archiviert</Badge> : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Aktiv</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {d.file_b64 && <a href={d.file_b64} download={d.filename}><Button size="icon" variant="ghost"><Download className="w-4 h-4" /></Button></a>}
                      <Button size="icon" variant="ghost" onClick={() => del(d.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Keine Dokumente</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>QM-Dokument</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titel</Label><Input data-testid="qm-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Beschreibung</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
            </div>
            <div><Label>Datei</Label><Input type="file" data-testid="qm-file" onChange={onFile} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="qm-save" onClick={save}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
